<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CustomToolCard from 'dashboard/components-next/captain/pageComponents/customTool/CustomToolCard.vue';
import CreateCustomToolDialog from 'dashboard/components-next/captain/pageComponents/customTool/CreateCustomToolDialog.vue';
import CreateDocumentDialog from 'dashboard/components-next/captain/pageComponents/document/CreateDocumentDialog.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';

const DEFAULT_SYSTEM_PROMPT = `Você é um agente de voz da empresa. Atenda de forma natural, cordial e objetiva.

Confirme informações importantes antes de executar ações. Consulte a base de conhecimento antes de responder sobre produtos, serviços ou políticas. Use as ferramentas HTTP somente quando forem necessárias para responder ou concluir a solicitação.

Nunca invente informações. Quando não souber ou não conseguir concluir uma ação, explique com clareza e ofereça transferência para um atendente humano.`;

const DEFAULT_TRAINING = {
  identity: '',
  business_context: '',
  target_audience: '',
  call_objective: '',
  conversation_style: 'Profissional, cordial, claro e empático.',
  required_information: '',
  forbidden_topics: '',
  success_criteria: '',
  fallback_instructions:
    'Tente esclarecer uma vez. Se não resolver ou o cliente pedir, transfira para atendimento humano.',
  rag_enabled: true,
  http_tools_enabled: true,
};

const DEFAULT_VOICE_DELIVERY = {
  speech_speed: 1,
  accent: 'neutral_br',
  tone: 'warm',
  pause_style: 'natural',
  expressiveness: 'balanced',
  pronunciation_notes: '',
};

const BUILT_IN_VOICES = [
  {
    id: 'gustavo',
    recommended: true,
    descriptionKey: 'CAPTAIN.VOICE_AGENT.VOICE.CATALOG.MARIN',
  },
];

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);
const assistantUiFlags = useMapGetter('captainAssistants/getUIFlags');
const documentUiFlags = useMapGetter('captainDocuments/getUIFlags');
const customToolUiFlags = useMapGetter('captainCustomTools/getUIFlags');
const documents = useMapGetter('captainDocuments/getRecords');
const customTools = useMapGetter('captainCustomTools/getRecords');

const isFetching = computed(() => assistantUiFlags.value.fetchingItem);
const isSaving = computed(() => assistantUiFlags.value.updatingItem);
const isLoadingDocuments = computed(() => documentUiFlags.value.fetchingList);
const isLoadingTools = computed(() => customToolUiFlags.value.fetchingList);

const activeSection = ref('training');
const systemPrompt = ref(DEFAULT_SYSTEM_PROMPT);
const training = ref({ ...DEFAULT_TRAINING });
const liveCalls = ref(false);
const realtimeModel = ref('qwen3-local');
const realtimeVoice = ref('gustavo');
const callGreeting = ref('');
const voiceDelivery = ref({ ...DEFAULT_VOICE_DELIVERY });
const customVoice = ref(null);
const voiceCapabilities = ref({
  loading: true,
  configured: true,
  custom: false,
});
const previewText = ref(
  'Olá! Que bom falar com você. Posso entender sua necessidade e encontrar o melhor próximo passo, com calma e objetividade.'
);
const previewingVoice = ref('');
const customVoiceName = ref('');
const consentRecording = ref(null);
const voiceSample = ref(null);
const consentConfirmed = ref(false);
const isCreatingCustomVoice = ref(false);
let previewAudio = null;
let previewAudioUrl = '';

const sections = computed(() => [
  {
    id: 'training',
    label: t('CAPTAIN.VOICE_AGENT.SECTIONS.TRAINING'),
    icon: 'i-lucide-brain-circuit',
  },
  {
    id: 'voice',
    label: t('CAPTAIN.VOICE_AGENT.SECTIONS.VOICE'),
    icon: 'i-lucide-audio-lines',
  },
  {
    id: 'knowledge',
    label: t('CAPTAIN.VOICE_AGENT.SECTIONS.KNOWLEDGE'),
    icon: 'i-lucide-book-open',
  },
  {
    id: 'tools',
    label: t('CAPTAIN.VOICE_AGENT.SECTIONS.TOOLS'),
    icon: 'i-lucide-wrench',
  },
  {
    id: 'test',
    label: t('CAPTAIN.VOICE_AGENT.SECTIONS.TEST'),
    icon: 'i-lucide-message-circle-more',
  },
]);

const modelOptions = [{ value: 'qwen3-local', label: 'Qwen3 Local PT-BR' }];

const voiceCatalog = computed(() =>
  BUILT_IN_VOICES.map(voice => ({
    ...voice,
    name: voice.id[0].toUpperCase() + voice.id.slice(1),
    description: t(
      voice.descriptionKey ||
        `CAPTAIN.VOICE_AGENT.VOICE.CATALOG.${voice.id.toUpperCase()}`
    ),
  }))
);

const voiceOptions = computed(() => {
  const options = BUILT_IN_VOICES.map(({ id }) => ({
    value: id,
    label: id[0].toUpperCase() + id.slice(1),
  }));
  if (realtimeModel.value !== 'qwen3-local' && customVoice.value?.id) {
    options.unshift({
      value: 'custom',
      label: customVoice.value.name,
    });
  }
  return options;
});

const accentOptions = computed(() =>
  ['neutral_br', 'paulista', 'carioca', 'mineiro', 'gaucho', 'nordestino'].map(
    value => ({
      value,
      label: t(`CAPTAIN.VOICE_AGENT.VOICE.ACCENTS.${value.toUpperCase()}`),
    })
  )
);

const toneOptions = computed(() =>
  ['warm', 'professional', 'consultative', 'energetic', 'calm'].map(value => ({
    value,
    label: t(`CAPTAIN.VOICE_AGENT.VOICE.TONES.${value.toUpperCase()}`),
  }))
);

const pauseOptions = computed(() =>
  ['light', 'natural', 'expressive'].map(value => ({
    value,
    label: t(`CAPTAIN.VOICE_AGENT.VOICE.PAUSES.${value.toUpperCase()}`),
  }))
);

const expressivenessOptions = computed(() =>
  ['low', 'balanced', 'high'].map(value => ({
    value,
    label: t(`CAPTAIN.VOICE_AGENT.VOICE.EXPRESSIVENESS.${value.toUpperCase()}`),
  }))
);

const scopedTools = computed(() =>
  customTools.value.filter(
    tool => !tool.assistant_id || tool.assistant_id === assistantId.value
  )
);

const hydrate = () => {
  const config = assistant.value?.config || {};
  const voiceTraining = config.voice_training || {};
  const whatsapp = config.whatsapp_capabilities || {};

  systemPrompt.value = config.instructions || DEFAULT_SYSTEM_PROMPT;
  training.value = {
    ...DEFAULT_TRAINING,
    ...voiceTraining,
    rag_enabled:
      voiceTraining.rag_enabled === undefined
        ? true
        : voiceTraining.rag_enabled,
    http_tools_enabled:
      voiceTraining.http_tools_enabled === undefined
        ? true
        : voiceTraining.http_tools_enabled,
  };
  liveCalls.value = !!whatsapp.live_calls;
  realtimeModel.value = 'qwen3-local';
  realtimeVoice.value = 'gustavo';
  callGreeting.value = whatsapp.call_greeting || '';
  voiceDelivery.value = {
    ...DEFAULT_VOICE_DELIVERY,
    ...(whatsapp.voice_delivery || {}),
  };
  customVoice.value = whatsapp.custom_voice || null;
};

const fetchDocuments = () =>
  store.dispatch('captainDocuments/get', {
    page: 1,
    assistantId: assistantId.value,
    sort: 'recently_updated',
  });

const fetchTools = () => store.dispatch('captainCustomTools/get', { page: 1 });

const fetchVoiceCapabilities = async () => {
  voiceCapabilities.value.loading = true;
  try {
    const { data } = await CaptainAssistantAPI.getVoiceCapabilities({
      assistantId: assistantId.value,
    });
    voiceCapabilities.value = {
      loading: false,
      configured: data.configured,
      custom: data.custom_voices,
    };
  } catch (error) {
    voiceCapabilities.value = {
      loading: false,
      configured: true,
      custom: false,
    };
  }
};

watch(
  assistantId,
  async id => {
    if (!id) return;
    try {
      await store.dispatch('captainAssistants/show', id);
      hydrate();
      const requests = [fetchDocuments(), fetchTools()];
      if (realtimeModel.value !== 'qwen3-local') {
        requests.push(fetchVoiceCapabilities());
      }
      await Promise.all(requests);
    } catch (error) {
      useAlert(t('CAPTAIN.VOICE_AGENT.LOAD_ERROR'));
    }
  },
  { immediate: true }
);

const handleSave = async () => {
  const currentConfig = assistant.value?.config || {};
  const currentWhatsapp = currentConfig.whatsapp_capabilities || {};
  try {
    await store.dispatch('captainAssistants/update', {
      id: assistantId.value,
      config: {
        ...currentConfig,
        instructions: systemPrompt.value.trim(),
        voice_training: { ...training.value },
        whatsapp_capabilities: {
          ...currentWhatsapp,
          live_calls: liveCalls.value,
          realtime_model: 'qwen3-local',
          realtime_voice: 'gustavo',
          call_greeting: callGreeting.value.trim() || null,
          voice_delivery: { ...voiceDelivery.value },
          custom_voice: customVoice.value || currentWhatsapp.custom_voice,
        },
      },
    });
    useAlert(t('CAPTAIN.VOICE_AGENT.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN.VOICE_AGENT.SAVE_ERROR'));
  }
};

const stopPreview = () => {
  if (previewAudio) {
    previewAudio.pause();
    previewAudio = null;
  }
  if (previewAudioUrl) {
    URL.revokeObjectURL(previewAudioUrl);
    previewAudioUrl = '';
  }
  previewingVoice.value = '';
};

const playVoicePreview = async voice => {
  if (realtimeModel.value === 'qwen3-local') return;

  if (previewingVoice.value === voice) {
    stopPreview();
    return;
  }
  stopPreview();
  previewingVoice.value = voice;
  try {
    const { data } = await CaptainAssistantAPI.previewVoice({
      assistantId: assistantId.value,
      voice,
      text: previewText.value.trim(),
      voiceDelivery: voiceDelivery.value,
    });
    previewAudioUrl = URL.createObjectURL(data);
    previewAudio = new Audio(previewAudioUrl);
    previewAudio.addEventListener('ended', stopPreview, { once: true });
    await previewAudio.play();
  } catch (error) {
    stopPreview();
    useAlert(t('CAPTAIN.VOICE_AGENT.VOICE.PREVIEW_ERROR'));
  }
};

const handleAudioFile = (event, target) => {
  const file = event.target.files?.[0] || null;
  if (file?.size > 10 * 1024 * 1024) {
    event.target.value = '';
    useAlert(t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.FILE_TOO_LARGE'));
    return;
  }
  if (target === 'consent') consentRecording.value = file;
  if (target === 'sample') voiceSample.value = file;
};

const createCustomVoice = async () => {
  if (
    !customVoiceName.value.trim() ||
    !consentRecording.value ||
    !voiceSample.value ||
    !consentConfirmed.value
  ) {
    useAlert(t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.REQUIRED_ERROR'));
    return;
  }

  isCreatingCustomVoice.value = true;
  try {
    const { data } = await CaptainAssistantAPI.createCustomVoice({
      assistantId: assistantId.value,
      name: customVoiceName.value.trim(),
      consentRecording: consentRecording.value,
      audioSample: voiceSample.value,
    });
    customVoice.value = data.custom_voice;
    realtimeVoice.value = 'custom';
    useAlert(t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.SUCCESS'));
  } catch (error) {
    const code = error.response?.data?.error;
    useAlert(
      code === 'custom_voice_not_available'
        ? t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.NOT_AVAILABLE')
        : t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.ERROR')
    );
  } finally {
    isCreatingCustomVoice.value = false;
  }
};

onBeforeUnmount(stopPreview);

const showDocumentDialog = ref(false);
const documentDialogRef = ref(null);
const openDocumentDialog = () => {
  showDocumentDialog.value = true;
  nextTick(() => documentDialogRef.value?.dialogRef.open());
};
const handleDocumentDialogClose = () => {
  showDocumentDialog.value = false;
};
const handleDocumentCreated = () => fetchDocuments();

const toolDialogType = ref('');
const selectedTool = ref(null);
const toolDialogRef = ref(null);
const deleteToolDialogRef = ref(null);

const openCreateToolDialog = () => {
  selectedTool.value = null;
  toolDialogType.value = 'create';
  nextTick(() => toolDialogRef.value?.dialogRef.open());
};

const openEditToolDialog = tool => {
  selectedTool.value = tool;
  toolDialogType.value = 'edit';
  nextTick(() => toolDialogRef.value?.dialogRef.open());
};

const openDeleteToolDialog = tool => {
  selectedTool.value = tool;
  nextTick(() => deleteToolDialogRef.value?.dialogRef.open());
};

const handleToolAction = ({ action, id }) => {
  const tool = scopedTools.value.find(item => item.id === id);
  if (!tool) return;
  if (action === 'edit') openEditToolDialog(tool);
  if (action === 'delete') openDeleteToolDialog(tool);
};

const handleToolDialogClose = () => {
  toolDialogType.value = '';
  selectedTool.value = null;
  fetchTools();
};

const testInput = ref('');
const testMessages = ref([]);
const isTesting = ref(false);

const runTest = async () => {
  const input = testInput.value.trim();
  if (!input || isTesting.value) return;

  const history = testMessages.value.map(message => ({
    role: message.role,
    content: message.content,
  }));
  testMessages.value.push({ role: 'user', content: input });
  testInput.value = '';
  isTesting.value = true;
  try {
    const { data } = await CaptainAssistantAPI.playground({
      assistantId: assistantId.value,
      messageContent: input,
      messageHistory: history,
    });
    testMessages.value.push({
      role: 'assistant',
      content: data.response || t('CAPTAIN.VOICE_AGENT.TEST.EMPTY_RESPONSE'),
    });
  } catch (error) {
    useAlert(t('CAPTAIN.VOICE_AGENT.TEST.ERROR'));
  } finally {
    isTesting.value = false;
  }
};

const documentStatus = document => {
  if (document.sync_in_progress)
    return t('CAPTAIN.VOICE_AGENT.KNOWLEDGE.SYNCING');
  if (document.status === 'available')
    return t('CAPTAIN.VOICE_AGENT.KNOWLEDGE.READY');
  return document.status || t('CAPTAIN.VOICE_AGENT.KNOWLEDGE.PROCESSING');
};
</script>

<template>
  <PageLayout
    :header-title="t('CAPTAIN.VOICE_AGENT.HEADER')"
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
    :feature-flag="FEATURE_FLAGS.CAPTAIN"
  >
    <template #headerActions>
      <div
        class="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-n-teal-2 text-n-teal-11 text-xs font-medium"
      >
        <span class="size-2 rounded-full bg-n-teal-9" />
        {{
          liveCalls
            ? t('CAPTAIN.VOICE_AGENT.STATUS.READY')
            : t('CAPTAIN.VOICE_AGENT.STATUS.INACTIVE')
        }}
      </div>
      <Button
        :label="t('CAPTAIN.VOICE_AGENT.SAVE')"
        icon="i-lucide-save"
        size="sm"
        :is-loading="isSaving"
        :disabled="isSaving"
        @click="handleSave"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-5 pb-8">
        <div
          class="flex overflow-x-auto gap-1 p-1 rounded-xl border border-n-weak bg-n-alpha-2"
        >
          <button
            v-for="section in sections"
            :key="section.id"
            type="button"
            class="flex flex-1 min-w-max items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
            :class="
              activeSection === section.id
                ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
            "
            @click="activeSection = section.id"
          >
            <span :class="section.icon" class="size-4" />
            {{ section.label }}
          </button>
        </div>

        <div
          v-if="activeSection === 'training'"
          class="grid gap-5 lg:grid-cols-[minmax(0,1fr)_19rem]"
        >
          <div class="flex flex-col gap-5">
            <section class="voice-panel">
              <div class="voice-panel__header">
                <div>
                  <h2 class="voice-panel__title">
                    {{ t('CAPTAIN.VOICE_AGENT.TRAINING.TITLE') }}
                  </h2>
                  <p class="voice-panel__description">
                    {{ t('CAPTAIN.VOICE_AGENT.TRAINING.DESCRIPTION') }}
                  </p>
                </div>
                <Button
                  :label="t('CAPTAIN.VOICE_AGENT.TRAINING.RESTORE_DEFAULT')"
                  icon="i-lucide-rotate-ccw"
                  color="slate"
                  variant="ghost"
                  size="sm"
                  @click="systemPrompt = DEFAULT_SYSTEM_PROMPT"
                />
              </div>
              <TextArea
                v-model="systemPrompt"
                :label="t('CAPTAIN.VOICE_AGENT.TRAINING.SYSTEM_PROMPT')"
                :placeholder="DEFAULT_SYSTEM_PROMPT"
                :rows="10"
                maxlength="12000"
                class="[&_textarea]:font-mono [&_textarea]:text-sm"
              />
              <span class="self-end text-xs text-n-slate-10">
                {{ `${systemPrompt.length} / 12000` }}
              </span>
            </section>

            <section class="voice-panel">
              <div class="voice-panel__header">
                <div>
                  <h2 class="voice-panel__title">
                    {{ t('CAPTAIN.VOICE_AGENT.TRAINING.STRUCTURED_TITLE') }}
                  </h2>
                  <p class="voice-panel__description">
                    {{
                      t('CAPTAIN.VOICE_AGENT.TRAINING.STRUCTURED_DESCRIPTION')
                    }}
                  </p>
                </div>
              </div>
              <div class="grid gap-4 md:grid-cols-2">
                <Input
                  v-model="training.identity"
                  :label="t('CAPTAIN.VOICE_AGENT.FIELDS.IDENTITY')"
                  :placeholder="t('CAPTAIN.VOICE_AGENT.PLACEHOLDERS.IDENTITY')"
                />
                <Input
                  v-model="training.target_audience"
                  :label="t('CAPTAIN.VOICE_AGENT.FIELDS.TARGET_AUDIENCE')"
                  :placeholder="
                    t('CAPTAIN.VOICE_AGENT.PLACEHOLDERS.TARGET_AUDIENCE')
                  "
                />
                <TextArea
                  v-model="training.business_context"
                  :label="t('CAPTAIN.VOICE_AGENT.FIELDS.BUSINESS_CONTEXT')"
                  :placeholder="
                    t('CAPTAIN.VOICE_AGENT.PLACEHOLDERS.BUSINESS_CONTEXT')
                  "
                  :rows="3"
                />
                <TextArea
                  v-model="training.call_objective"
                  :label="t('CAPTAIN.VOICE_AGENT.FIELDS.CALL_OBJECTIVE')"
                  :placeholder="
                    t('CAPTAIN.VOICE_AGENT.PLACEHOLDERS.CALL_OBJECTIVE')
                  "
                  :rows="3"
                />
                <TextArea
                  v-model="training.conversation_style"
                  :label="t('CAPTAIN.VOICE_AGENT.FIELDS.CONVERSATION_STYLE')"
                  :rows="3"
                />
                <TextArea
                  v-model="training.required_information"
                  :label="t('CAPTAIN.VOICE_AGENT.FIELDS.REQUIRED_INFORMATION')"
                  :placeholder="
                    t('CAPTAIN.VOICE_AGENT.PLACEHOLDERS.REQUIRED_INFORMATION')
                  "
                  :rows="3"
                />
                <TextArea
                  v-model="training.forbidden_topics"
                  :label="t('CAPTAIN.VOICE_AGENT.FIELDS.FORBIDDEN_TOPICS')"
                  :placeholder="
                    t('CAPTAIN.VOICE_AGENT.PLACEHOLDERS.FORBIDDEN_TOPICS')
                  "
                  :rows="3"
                />
                <TextArea
                  v-model="training.success_criteria"
                  :label="t('CAPTAIN.VOICE_AGENT.FIELDS.SUCCESS_CRITERIA')"
                  :placeholder="
                    t('CAPTAIN.VOICE_AGENT.PLACEHOLDERS.SUCCESS_CRITERIA')
                  "
                  :rows="3"
                />
                <TextArea
                  v-model="training.fallback_instructions"
                  :label="t('CAPTAIN.VOICE_AGENT.FIELDS.FALLBACK')"
                  :rows="3"
                  class="md:col-span-2"
                />
              </div>
            </section>
          </div>

          <aside class="flex flex-col gap-5">
            <section class="voice-panel sticky top-4">
              <div class="voice-panel__header">
                <div>
                  <h2 class="voice-panel__title">
                    {{ t('CAPTAIN.VOICE_AGENT.VOICE.TITLE') }}
                  </h2>
                  <p class="voice-panel__description">
                    {{ t('CAPTAIN.VOICE_AGENT.VOICE.DESCRIPTION') }}
                  </p>
                </div>
              </div>
              <div
                class="flex items-center justify-between gap-4 p-3 rounded-lg bg-n-alpha-2"
              >
                <div class="flex flex-col gap-1">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('CAPTAIN.VOICE_AGENT.VOICE.LIVE_CALLS') }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{ t('CAPTAIN.VOICE_AGENT.VOICE.LIVE_CALLS_HINT') }}
                  </span>
                </div>
                <Switch v-model="liveCalls" />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.MODEL') }}
                </label>
                <ComboBox
                  v-model="realtimeModel"
                  :options="modelOptions"
                  class="[&>div>button]:bg-n-alpha-black2"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.SELECTED_VOICE') }}
                </label>
                <ComboBox
                  v-model="realtimeVoice"
                  :options="voiceOptions"
                  class="[&>div>button]:bg-n-alpha-black2"
                />
              </div>
              <TextArea
                v-model="callGreeting"
                :label="t('CAPTAIN.VOICE_AGENT.VOICE.GREETING')"
                :placeholder="
                  t('CAPTAIN.VOICE_AGENT.VOICE.GREETING_PLACEHOLDER')
                "
                :rows="4"
                maxlength="1000"
              />
              <Button
                :label="t('CAPTAIN.VOICE_AGENT.VOICE.TEST_AGENT')"
                icon="i-lucide-audio-lines"
                color="teal"
                variant="outline"
                class="w-full"
                @click="activeSection = 'test'"
              />
            </section>
          </aside>
        </div>

        <div v-else-if="activeSection === 'voice'" class="flex flex-col gap-5">
          <section class="voice-panel">
            <div class="voice-panel__header">
              <div>
                <h2 class="voice-panel__title">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.CATALOG_TITLE') }}
                </h2>
                <p class="voice-panel__description">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.CATALOG_DESCRIPTION') }}
                </p>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-xs text-n-slate-11">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.MODEL') }}
                </span>
                <ComboBox
                  v-model="realtimeModel"
                  :options="modelOptions"
                  class="min-w-48 [&>div>button]:bg-n-alpha-black2"
                />
              </div>
            </div>

            <TextArea
              v-if="realtimeModel !== 'qwen3-local'"
              v-model="previewText"
              :label="t('CAPTAIN.VOICE_AGENT.VOICE.PREVIEW_TEXT')"
              :rows="2"
              maxlength="400"
            />

            <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <button
                v-for="voice in voiceCatalog"
                :key="voice.id"
                type="button"
                class="voice-card"
                :class="{ 'voice-card--selected': realtimeVoice === voice.id }"
                @click="realtimeVoice = voice.id"
              >
                <div class="flex items-center justify-between gap-2">
                  <span class="font-medium text-n-slate-12">
                    {{ voice.name }}
                  </span>
                  <span
                    v-if="voice.recommended"
                    class="rounded-full bg-n-teal-3 px-2 py-0.5 text-[10px] font-medium text-n-teal-11"
                  >
                    {{ t('CAPTAIN.VOICE_AGENT.VOICE.RECOMMENDED') }}
                  </span>
                </div>
                <p class="m-0 min-h-10 text-left text-xs text-n-slate-11">
                  {{ voice.description }}
                </p>
                <span
                  v-if="realtimeModel !== 'qwen3-local'"
                  class="flex items-center gap-1.5 text-xs font-medium text-n-blue-11"
                  @click.stop="playVoicePreview(voice.id)"
                >
                  <span
                    :class="
                      previewingVoice === voice.id
                        ? 'i-lucide-square'
                        : 'i-lucide-play'
                    "
                    class="size-3.5"
                  />
                  {{
                    previewingVoice === voice.id
                      ? t('CAPTAIN.VOICE_AGENT.VOICE.STOP_PREVIEW')
                      : t('CAPTAIN.VOICE_AGENT.VOICE.PLAY_PREVIEW')
                  }}
                </span>
              </button>

              <button
                v-if="realtimeModel !== 'qwen3-local' && customVoice?.id"
                type="button"
                class="voice-card voice-card--custom"
                :class="{ 'voice-card--selected': realtimeVoice === 'custom' }"
                @click="realtimeVoice = 'custom'"
              >
                <div class="flex items-center justify-between gap-2">
                  <span class="font-medium text-n-slate-12">
                    {{ customVoice.name }}
                  </span>
                  <span class="i-lucide-sparkles size-4 text-n-teal-10" />
                </div>
                <p class="m-0 min-h-10 text-left text-xs text-n-slate-11">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.ACTIVE_DESCRIPTION') }}
                </p>
                <span
                  class="flex items-center gap-1.5 text-xs font-medium text-n-blue-11"
                  @click.stop="playVoicePreview('custom')"
                >
                  <span
                    :class="
                      previewingVoice === 'custom'
                        ? 'i-lucide-square'
                        : 'i-lucide-play'
                    "
                    class="size-3.5"
                  />
                  {{
                    previewingVoice === 'custom'
                      ? t('CAPTAIN.VOICE_AGENT.VOICE.STOP_PREVIEW')
                      : t('CAPTAIN.VOICE_AGENT.VOICE.PLAY_PREVIEW')
                  }}
                </span>
              </button>
            </div>
            <p class="m-0 text-xs text-n-slate-10">
              {{ t('CAPTAIN.VOICE_AGENT.VOICE.AI_DISCLOSURE') }}
            </p>
          </section>

          <section class="voice-panel">
            <div class="voice-panel__header">
              <div>
                <h2 class="voice-panel__title">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.HUMANIZATION_TITLE') }}
                </h2>
                <p class="voice-panel__description">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.HUMANIZATION_DESCRIPTION') }}
                </p>
              </div>
              <Button
                v-if="realtimeModel !== 'qwen3-local'"
                :label="t('CAPTAIN.VOICE_AGENT.VOICE.PREVIEW_SELECTED')"
                icon="i-lucide-volume-2"
                color="teal"
                variant="outline"
                size="sm"
                :is-loading="previewingVoice === realtimeVoice"
                @click="playVoicePreview(realtimeVoice)"
              />
            </div>

            <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div class="flex flex-col gap-2">
                <div class="flex items-center justify-between gap-2">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ t('CAPTAIN.VOICE_AGENT.VOICE.SPEED') }}
                  </label>
                  <span class="text-xs font-medium text-n-blue-11">
                    {{ `${Number(voiceDelivery.speech_speed).toFixed(2)}×` }}
                  </span>
                </div>
                <input
                  v-model.number="voiceDelivery.speech_speed"
                  type="range"
                  min="0.75"
                  max="1.25"
                  step="0.05"
                  class="w-full accent-blue-600"
                />
                <div class="flex justify-between text-[10px] text-n-slate-10">
                  <span>{{ t('CAPTAIN.VOICE_AGENT.VOICE.SLOW') }}</span>
                  <span>{{ t('CAPTAIN.VOICE_AGENT.VOICE.FAST') }}</span>
                </div>
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.ACCENT') }}
                </label>
                <ComboBox
                  v-model="voiceDelivery.accent"
                  :options="accentOptions"
                  class="[&>div>button]:bg-n-alpha-black2"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.TONE') }}
                </label>
                <ComboBox
                  v-model="voiceDelivery.tone"
                  :options="toneOptions"
                  class="[&>div>button]:bg-n-alpha-black2"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.PAUSE_STYLE') }}
                </label>
                <ComboBox
                  v-model="voiceDelivery.pause_style"
                  :options="pauseOptions"
                  class="[&>div>button]:bg-n-alpha-black2"
                />
              </div>

              <div class="flex flex-col gap-1 md:col-span-2">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.EXPRESSIVENESS_LABEL') }}
                </label>
                <ComboBox
                  v-model="voiceDelivery.expressiveness"
                  :options="expressivenessOptions"
                  class="[&>div>button]:bg-n-alpha-black2"
                />
              </div>

              <TextArea
                v-model="voiceDelivery.pronunciation_notes"
                :label="t('CAPTAIN.VOICE_AGENT.VOICE.PRONUNCIATION_NOTES')"
                :placeholder="
                  t('CAPTAIN.VOICE_AGENT.VOICE.PRONUNCIATION_PLACEHOLDER')
                "
                :rows="3"
                maxlength="1000"
                class="md:col-span-2"
              />
            </div>
          </section>

          <section v-if="realtimeModel !== 'qwen3-local'" class="voice-panel">
            <div class="voice-panel__header">
              <div>
                <h2 class="voice-panel__title">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.TITLE') }}
                </h2>
                <p class="voice-panel__description">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.DESCRIPTION') }}
                </p>
              </div>
              <span
                v-if="customVoice?.id"
                class="rounded-full bg-n-teal-3 px-3 py-1 text-xs font-medium text-n-teal-11"
              >
                {{ t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.CONFIGURED') }}
              </span>
            </div>

            <div
              v-if="voiceCapabilities.loading"
              class="flex items-center justify-center gap-2 py-8 text-sm text-n-slate-11"
            >
              <Spinner class="size-4" />
              {{ t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.CHECKING') }}
            </div>

            <div
              v-else-if="!voiceCapabilities.configured"
              class="voice-notice voice-notice--warning"
            >
              <span class="i-lucide-key-round size-5 shrink-0" />
              <p class="m-0 text-sm">
                {{ t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.NOT_CONFIGURED') }}
              </p>
            </div>

            <div
              v-else-if="!voiceCapabilities.custom"
              class="voice-notice voice-notice--warning"
            >
              <span class="i-lucide-lock-keyhole size-5 shrink-0" />
              <p class="m-0 text-sm">
                {{ t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.NOT_AVAILABLE') }}
              </p>
            </div>

            <div v-else class="grid gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
              <div class="flex flex-col gap-4">
                <Input
                  v-model="customVoiceName"
                  :label="t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.NAME')"
                  :placeholder="
                    t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.NAME_PLACEHOLDER')
                  "
                />

                <div class="grid gap-3 sm:grid-cols-2">
                  <label class="voice-upload">
                    <span class="i-lucide-file-audio size-5 text-n-blue-10" />
                    <span class="min-w-0">
                      <strong class="block text-sm text-n-slate-12">
                        {{ t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.CONSENT_FILE') }}
                      </strong>
                      <span class="block truncate text-xs text-n-slate-10">
                        {{
                          consentRecording?.name ||
                          t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.SELECT_FILE')
                        }}
                      </span>
                    </span>
                    <input
                      type="file"
                      class="sr-only"
                      accept="audio/mpeg,audio/wav,audio/x-wav,audio/ogg,audio/aac,audio/flac,audio/webm,audio/mp4"
                      @change="handleAudioFile($event, 'consent')"
                    />
                  </label>

                  <label class="voice-upload">
                    <span class="i-lucide-mic-2 size-5 text-n-teal-10" />
                    <span class="min-w-0">
                      <strong class="block text-sm text-n-slate-12">
                        {{ t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.SAMPLE_FILE') }}
                      </strong>
                      <span class="block truncate text-xs text-n-slate-10">
                        {{
                          voiceSample?.name ||
                          t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.SELECT_FILE')
                        }}
                      </span>
                    </span>
                    <input
                      type="file"
                      class="sr-only"
                      accept="audio/mpeg,audio/wav,audio/x-wav,audio/ogg,audio/aac,audio/flac,audio/webm,audio/mp4"
                      @change="handleAudioFile($event, 'sample')"
                    />
                  </label>
                </div>

                <label class="flex items-start gap-2 text-sm text-n-slate-11">
                  <input
                    v-model="consentConfirmed"
                    type="checkbox"
                    class="mt-0.5 size-4 rounded accent-blue-600"
                  />
                  <span>
                    {{ t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.CONFIRM') }}
                  </span>
                </label>

                <Button
                  :label="t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.CREATE')"
                  icon="i-lucide-sparkles"
                  :is-loading="isCreatingCustomVoice"
                  :disabled="isCreatingCustomVoice"
                  class="self-start"
                  @click="createCustomVoice"
                />
              </div>

              <aside class="voice-consent-box">
                <div
                  class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
                >
                  <span class="i-lucide-shield-check size-4 text-n-teal-10" />
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.CONSENT_TITLE') }}
                </div>
                <p class="m-0 text-sm leading-6 text-n-slate-11">
                  {{
                    `“${t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.CONSENT_PHRASE')}”`
                  }}
                </p>
                <p class="m-0 text-xs text-n-slate-10">
                  {{ t('CAPTAIN.VOICE_AGENT.VOICE.CUSTOM.REQUIREMENTS') }}
                </p>
              </aside>
            </div>
          </section>
        </div>

        <section v-else-if="activeSection === 'knowledge'" class="voice-panel">
          <div class="voice-panel__header">
            <div>
              <h2 class="voice-panel__title">
                {{ t('CAPTAIN.VOICE_AGENT.KNOWLEDGE.TITLE') }}
              </h2>
              <p class="voice-panel__description">
                {{ t('CAPTAIN.VOICE_AGENT.KNOWLEDGE.DESCRIPTION') }}
              </p>
            </div>
            <div class="flex items-center gap-3">
              <div class="flex items-center gap-2">
                <span class="text-xs text-n-slate-11">
                  {{ t('CAPTAIN.VOICE_AGENT.KNOWLEDGE.USE_IN_CALLS') }}
                </span>
                <Switch v-model="training.rag_enabled" />
              </div>
              <Button
                :label="t('CAPTAIN.VOICE_AGENT.KNOWLEDGE.ADD')"
                icon="i-lucide-upload"
                size="sm"
                @click="openDocumentDialog"
              />
            </div>
          </div>

          <div v-if="isLoadingDocuments" class="flex justify-center py-12">
            <Spinner />
          </div>
          <div
            v-else-if="!documents.length"
            class="flex flex-col items-center gap-3 py-12 text-center"
          >
            <span class="i-lucide-file-up size-8 text-n-slate-9" />
            <div>
              <p class="m-0 text-sm font-medium text-n-slate-12">
                {{ t('CAPTAIN.VOICE_AGENT.KNOWLEDGE.EMPTY_TITLE') }}
              </p>
              <p class="m-0 mt-1 text-sm text-n-slate-11">
                {{ t('CAPTAIN.VOICE_AGENT.KNOWLEDGE.EMPTY_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div v-else class="flex flex-col gap-2">
            <div
              v-for="document in documents"
              :key="document.id"
              class="flex items-center justify-between gap-4 p-4 rounded-lg border border-n-weak"
            >
              <div class="flex items-center gap-3 min-w-0">
                <span
                  :class="
                    document.pdf_document
                      ? 'i-lucide-file-text'
                      : 'i-lucide-link'
                  "
                  class="size-5 shrink-0 text-n-slate-10"
                />
                <div class="min-w-0">
                  <p class="m-0 text-sm font-medium truncate text-n-slate-12">
                    {{ document.name || document.external_link }}
                  </p>
                  <p class="m-0 mt-0.5 text-xs truncate text-n-slate-11">
                    {{
                      document.external_link ||
                      t('CAPTAIN.VOICE_AGENT.KNOWLEDGE.PDF')
                    }}
                  </p>
                </div>
              </div>
              <span
                class="px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-11 shrink-0"
              >
                {{ documentStatus(document) }}
              </span>
            </div>
          </div>
        </section>

        <section v-else-if="activeSection === 'tools'" class="voice-panel">
          <div class="voice-panel__header">
            <div>
              <h2 class="voice-panel__title">
                {{ t('CAPTAIN.VOICE_AGENT.TOOLS.TITLE') }}
              </h2>
              <p class="voice-panel__description">
                {{ t('CAPTAIN.VOICE_AGENT.TOOLS.DESCRIPTION') }}
              </p>
            </div>
            <div class="flex items-center gap-3">
              <div class="flex items-center gap-2">
                <span class="text-xs text-n-slate-11">
                  {{ t('CAPTAIN.VOICE_AGENT.TOOLS.USE_IN_CALLS') }}
                </span>
                <Switch v-model="training.http_tools_enabled" />
              </div>
              <Button
                :label="t('CAPTAIN.VOICE_AGENT.TOOLS.ADD')"
                icon="i-lucide-plus"
                size="sm"
                @click="openCreateToolDialog"
              />
            </div>
          </div>

          <div
            class="flex items-start gap-3 p-4 rounded-lg bg-n-blue-2 text-n-blue-11"
          >
            <span class="i-lucide-shield-check size-5 shrink-0 mt-0.5" />
            <p class="m-0 text-sm">
              {{ t('CAPTAIN.VOICE_AGENT.TOOLS.SECURITY_NOTE') }}
            </p>
          </div>

          <div v-if="isLoadingTools" class="flex justify-center py-12">
            <Spinner />
          </div>
          <div
            v-else-if="!scopedTools.length"
            class="flex flex-col items-center gap-3 py-12 text-center"
          >
            <span class="i-lucide-plug-zap size-8 text-n-slate-9" />
            <div>
              <p class="m-0 text-sm font-medium text-n-slate-12">
                {{ t('CAPTAIN.VOICE_AGENT.TOOLS.EMPTY_TITLE') }}
              </p>
              <p class="m-0 mt-1 text-sm text-n-slate-11">
                {{ t('CAPTAIN.VOICE_AGENT.TOOLS.EMPTY_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div v-else class="flex flex-col gap-3">
            <CustomToolCard
              v-for="tool in scopedTools"
              :id="tool.id"
              :key="tool.id"
              :title="tool.title"
              :description="tool.description"
              :auth-type="tool.auth_type"
              :created-at="tool.created_at"
              :updated-at="tool.updated_at"
              @action="handleToolAction"
            />
          </div>
        </section>

        <section v-else class="voice-panel">
          <div class="voice-panel__header">
            <div>
              <h2 class="voice-panel__title">
                {{ t('CAPTAIN.VOICE_AGENT.TEST.TITLE') }}
              </h2>
              <p class="voice-panel__description">
                {{ t('CAPTAIN.VOICE_AGENT.TEST.DESCRIPTION') }}
              </p>
            </div>
            <Button
              v-if="testMessages.length"
              :label="t('CAPTAIN.VOICE_AGENT.TEST.CLEAR')"
              icon="i-lucide-trash-2"
              color="slate"
              variant="ghost"
              size="sm"
              @click="testMessages = []"
            />
          </div>

          <div
            class="flex flex-col gap-3 min-h-72 max-h-[28rem] overflow-y-auto p-4 rounded-xl border border-n-weak bg-n-alpha-1"
          >
            <div
              v-if="!testMessages.length"
              class="flex flex-1 flex-col items-center justify-center gap-2 text-center text-n-slate-11"
            >
              <span class="i-lucide-messages-square size-8" />
              <p class="m-0 text-sm">
                {{ t('CAPTAIN.VOICE_AGENT.TEST.EMPTY') }}
              </p>
            </div>
            <div
              v-for="(message, index) in testMessages"
              :key="index"
              class="max-w-[80%] px-4 py-3 rounded-xl text-sm whitespace-pre-wrap"
              :class="
                message.role === 'user'
                  ? 'self-end bg-n-blue-9 text-white'
                  : 'self-start bg-n-alpha-3 text-n-slate-12'
              "
            >
              {{ message.content }}
            </div>
            <div
              v-if="isTesting"
              class="self-start flex items-center gap-2 px-4 py-3 rounded-xl bg-n-alpha-3 text-n-slate-11 text-sm"
            >
              <Spinner class="size-4" />
              {{ t('CAPTAIN.VOICE_AGENT.TEST.THINKING') }}
            </div>
          </div>

          <form class="flex gap-3 items-end" @submit.prevent="runTest">
            <TextArea
              v-model="testInput"
              :label="t('CAPTAIN.VOICE_AGENT.TEST.INPUT_LABEL')"
              :placeholder="t('CAPTAIN.VOICE_AGENT.TEST.INPUT_PLACEHOLDER')"
              :rows="3"
              class="flex-1"
            />
            <Button
              type="submit"
              :label="t('CAPTAIN.VOICE_AGENT.TEST.SEND')"
              icon="i-lucide-send"
              :is-loading="isTesting"
              :disabled="isTesting || !testInput.trim()"
            />
          </form>
          <p class="m-0 text-xs text-n-slate-10">
            {{ t('CAPTAIN.VOICE_AGENT.TEST.SAVE_HINT') }}
          </p>
        </section>
      </div>
    </template>
  </PageLayout>

  <CreateDocumentDialog
    v-if="showDocumentDialog"
    ref="documentDialogRef"
    :assistant-id="assistantId"
    @create-success="handleDocumentCreated"
    @close="handleDocumentDialogClose"
  />

  <CreateCustomToolDialog
    v-if="toolDialogType"
    ref="toolDialogRef"
    :assistant-id="assistantId"
    :type="toolDialogType"
    :selected-tool="selectedTool"
    @close="handleToolDialogClose"
  />

  <DeleteDialog
    v-if="selectedTool"
    ref="deleteToolDialogRef"
    :entity="selectedTool"
    type="CustomTools"
    translation-key="CUSTOM_TOOLS"
    @delete-success="fetchTools"
  />
</template>

<style scoped>
.voice-panel {
  @apply flex flex-col gap-5 p-5 rounded-xl border border-n-weak bg-n-solid-2;
}

.voice-panel__header {
  @apply flex flex-col sm:flex-row sm:items-start justify-between gap-3;
}

.voice-panel__title {
  @apply m-0 text-base font-medium text-n-slate-12;
}

.voice-panel__description {
  @apply m-0 mt-1 text-sm text-n-slate-11;
}

.voice-card {
  @apply flex min-h-36 flex-col justify-between gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-4 text-left transition-all hover:border-n-blue-7 hover:bg-n-alpha-2;
}

.voice-card--selected {
  @apply border-n-blue-8 bg-n-blue-2 ring-1 ring-n-blue-7;
}

.voice-card--custom {
  @apply border-dashed border-n-teal-7 bg-n-teal-2;
}

.voice-upload {
  @apply flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-n-weak bg-n-alpha-1 p-4 transition-colors hover:border-n-blue-7 hover:bg-n-alpha-2;
}

.voice-consent-box {
  @apply flex flex-col gap-3 rounded-xl border border-n-weak bg-n-alpha-2 p-4;
}

.voice-notice {
  @apply flex items-start gap-3 rounded-xl border p-4;
}

.voice-notice--warning {
  @apply border-n-amber-6 bg-n-amber-2 text-n-amber-11;
}
</style>
